import React from 'react';
import { FileText } from 'lucide-react';
import { DealPaymentsRecap } from './DealPaymentsRecap';
import type { DealServiceFormData, Service } from '../../../lib/types';

interface DealStep2BillingTabProps {
  language: string;
  singleInvoiceMode: boolean;
  setSingleInvoiceMode: (v: boolean) => void;
  /** Metodi di pagamento della org. */
  paymentMethods?: Array<{ id: string; name: string; archived?: boolean }>;
  /** Metodo con cui si incassano le righe prodotto dell'offerta. */
  paymentMethodId?: string | null;
  onPaymentMethodChange?: (id: string | null) => void;
  /** Il selettore compare solo se l'offerta contiene righe prodotto. */
  hasProductLines?: boolean;
  /** Righe dell'offerta e catalogo servizi, per il riepilogo pagamenti. */
  serviceItems?: DealServiceFormData[];
  services?: Service[];
  /** Apre l'editor di una riga dal riepilogo. */
  onOpenLine?: (index: number) => void;
}

/**
 * Step 2 — tab "Fatturazione": le condizioni con cui l'offerta diventa fattura.
 * Ospita il metodo di pagamento delle righe prodotto — che non ne hanno uno
 * proprio, perché la fatturazione le emette come saldo unico alla firma — e il
 * toggle "fattura unica con righe multiple".
 *
 * La data di emissione e le scadenze delle singole righe stanno invece dentro
 * `DealCommissionSection` (tab Commissioni), dove sono finite storicamente.
 */
export function DealStep2BillingTab({
  language,
  singleInvoiceMode,
  setSingleInvoiceMode,
  paymentMethods = [],
  paymentMethodId,
  onPaymentMethodChange,
  hasProductLines = false,
  serviceItems = [],
  services = [],
  onOpenLine,
}: DealStep2BillingTabProps) {
  // Un metodo archiviato resta in elenco finché è quello scelto: toglierlo
  // farebbe sparire il valore dell'offerta senza spiegazioni.
  const selectableMethods = paymentMethods.filter(m => !m.archived || m.id === paymentMethodId);
  return (
    <div className="flex-1 overflow-y-auto p-4">
      {/* Due colonne da lg in su: le impostazioni sono poche e di larghezza
          fissa, il riepilogo si prende tutto il resto perche' e' l'unica cosa
          che cresce con le righe dell'offerta. Sotto lg impilano, con le
          impostazioni prima: si sceglie, poi si verifica. `items-start` evita
          che la card corta si stiri fino all'altezza dell'altra. */}
      <div className="w-full max-w-7xl grid grid-cols-1 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)] gap-4 items-start">
        <div className="glass-card rounded-lg overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3 border-b border-black/[0.06] dark:border-white/10">
            <FileText className="h-4 w-4 text-black/70 dark:text-white/70" />
            <h3 className="text-[14px] font-semibold text-black dark:text-white">
              {language === 'it' ? 'Impostazioni Fatturazione' : 'Billing Settings'}
            </h3>
          </div>

          {/* Pagamento delle righe prodotto: non hanno un metodo proprio, la
              fatturazione le emette come saldo unico alla firma. Compare solo
              se l'offerta contiene prodotti. */}
          {hasProductLines && (
            <div className="px-5 py-4 border-b border-black/[0.06] dark:border-white/10">
              <label
                htmlFor="productPaymentMethod"
                className="block text-[13px] font-medium text-black dark:text-white mb-1.5"
              >
                {language === 'it' ? 'Pagamento delle righe prodotto' : 'Product lines payment'}
              </label>
              <select
                id="productPaymentMethod"
                value={paymentMethodId || ''}
                onChange={(e) => onPaymentMethodChange?.(e.target.value || null)}
                className={`w-full max-w-xs px-2.5 py-1.5 text-[13px] rounded-md border bg-white dark:bg-gray-800/60 text-black dark:text-white focus:border-[#2383E2] focus:outline-none focus:ring-2 focus:ring-[#2383E2]/20 transition-colors ${
                  paymentMethodId
                    ? 'border-black/[0.12] dark:border-white/15'
                    : 'border-amber-400/70'
                }`}
              >
                <option value="">{language === 'it' ? 'Da scegliere…' : 'To be chosen…'}</option>
                {selectableMethods.map(m => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
              <p className="text-[12px] text-black/55 dark:text-white/55 mt-1.5">
                {language === 'it'
                  ? 'I prodotti sono un saldo unico alla firma dell’offerta. I servizi tengono il metodo e le rate della propria riga.'
                  : 'Products settle in full when the deal is signed. Services keep the method and schedule of their own line.'}
              </p>
            </div>
          )}

          <div className="px-5 py-4">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="singleInvoiceMode"
                checked={singleInvoiceMode}
                onChange={(e) => setSingleInvoiceMode(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <div>
                <label htmlFor="singleInvoiceMode" className="text-[13px] font-medium text-black dark:text-white cursor-pointer">
                  {language === 'it' ? "Crea un'unica fattura con più righe" : 'Create a single invoice with multiple lines'}
                </label>
                <p className="text-[12px] text-black/55 dark:text-white/55 mt-1">
                  {language === 'it'
                    ? 'Genera una sola fattura con tutte le rate come righe separate. Ogni rata avrà la propria scadenza di pagamento.'
                    : 'Generate a single invoice with all installments as separate lines. Each installment will have its own payment due date.'}
                </p>
              </div>
            </div>
          </div>
        </div>

        <DealPaymentsRecap
          language={language}
          serviceItems={serviceItems}
          services={services}
          paymentMethods={paymentMethods}
          productPaymentMethodId={paymentMethodId}
          onOpenLine={onOpenLine}
        />
      </div>
    </div>
  );
}
